"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import ArrowBackIosOutlinedIcon from "@mui/icons-material/ArrowBackIosOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { ChevronLeft, ChevronDown } from "lucide-react";
import { message } from "antd";
import "ag-charts-community";
import "./marts.css";
import "./summary.css";
import { BASE_URL } from "../apiConfig";
import FiltersSection from "./martsfilters";
import {
  ClientSideRowModelModule,
  CsvExportModule,
  NumberFilterModule,
  TextFilterModule,
  ValidationModule,
  ModuleRegistry,
  themeQuartz,
} from "ag-grid-community";
import {
  ColumnMenuModule,
  ContextMenuModule,
  ExcelExportModule,
  MultiFilterModule,
  SetFilterModule,
  SideBarModule,
  GridChartsModule,
  RangeSelectionModule,
  RowGroupingModule,
  MenuModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  StatusBarModule,
  RichSelectModule,
} from "ag-grid-enterprise";
import debounce from "lodash/debounce";

ModuleRegistry.registerModules([
  TextFilterModule,
  NumberFilterModule,
  ClientSideRowModelModule,
  CsvExportModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  MultiFilterModule,
  // SetFilterModule, // Temporarily disable to force date filters
  ValidationModule,
  SideBarModule,
  GridChartsModule,
  RangeSelectionModule,
  RowGroupingModule,
  MenuModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  StatusBarModule,
  RichSelectModule,
]);

const myTheme = themeQuartz.withParams({
  sideBarBackgroundColor: "#f8f9fa",
  sideButtonBarBackgroundColor: "#ffffff",
  sideButtonBarTopPadding: 20,
  sideButtonSelectedUnderlineColor: "orange",
  sideButtonTextColor: "#495057",
  sideButtonHoverBackgroundColor: "#e9ecef",
  sideButtonSelectedBackgroundColor: "#fff3cd",
  sideButtonHoverTextColor: "#212529",
  sideButtonSelectedTextColor: "#856404",
  sideButtonSelectedBorder: false,
  floatingFilterBackgroundColor: "#ffffff",
  headerBackgroundColor: "#ffffff",
});

const getRowStyle = (params) => {
  if (params.node.rowIndex === 1) {
    return {
      backgroundColor: "",
    };
  }
  return null;
};

// Helper function to determine if a value is a valid date
const isValidDate = (value) => {
  if (!value || value === "-") return false;

  // Check for various date formats
  const datePatterns = [
    /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO datetime
    /^\d{2}\/\d{2}\/\d{4}$/, // MM/DD/YYYY or DD/MM/YYYY
    /^\d{2}-\d{2}-\d{4}$/, // MM-DD-YYYY or DD-MM-YYYY
  ];

  const stringValue = String(value).trim();
  const hasDatePattern = datePatterns.some((pattern) =>
    pattern.test(stringValue)
  );

  if (hasDatePattern) {
    const date = new Date(value);
    return !isNaN(date.getTime());
  }

  return false;
};

// Helper function to parse date values consistently
const parseDate = (value) => {
  if (!value || value === "-") return null;

  // If it's already a Date object, return it
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  // If it's a string or number, try to parse it
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

// Helper function to convert date strings to actual Date objects for grid data
const convertToDateObject = (value) => {
  if (!value || value === "-") return value;

  const parsedDate = parseDate(value);
  return parsedDate || value;
};

// Custom date comparator for AG-Grid
const dateComparator = (filterDate, cellValue) => {
  if (!cellValue) return -1;

  // Parse and validate both dates
  let parsedFilterDate;
  if (filterDate instanceof Date) {
    parsedFilterDate = filterDate;
  } else {
    parsedFilterDate = parseDate(filterDate);
  }

  if (!parsedFilterDate || isNaN(parsedFilterDate.getTime())) return -1;

  const cellDate = parseDate(cellValue);
  if (!cellDate || isNaN(cellDate.getTime())) return -1;

  // Compare only the date part (ignore time)
  const cellDateOnly = new Date(
    cellDate.getFullYear(),
    cellDate.getMonth(),
    cellDate.getDate()
  );
  const filterDateOnly = new Date(
    parsedFilterDate.getFullYear(),
    parsedFilterDate.getMonth(),
    parsedFilterDate.getDate()
  );

  if (cellDateOnly < filterDateOnly) return -1;
  if (cellDateOnly > filterDateOnly) return 1;
  return 0;
};

// Custom value formatter for dates
const dateValueFormatter = (params) => {
  if (!params.value) return "";

  const date = parseDate(params.value);
  if (!date) return params.value;

  // Format as DD/MM/YYYY
  return date.toLocaleDateString("en-GB");
};

// Custom value formatter for datetime
const dateTimeValueFormatter = (params) => {
  if (!params.value) return "";

  const date = parseDate(params.value);
  if (!date) return params.value;

  // Format as DD/MM/YYYY HH:MM:SS
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

// Formats an ag-grid style filter model (as stored against a saved child-mart filter) into display text
const filterOperatorLabels = {
  equals: "",
  notEqual: "≠ ",
  greaterThan: "> ",
  greaterThanOrEqual: "≥ ",
  lessThan: "< ",
  lessThanOrEqual: "≤ ",
  contains: "Contains ",
  notContains: "Does not contain ",
  startsWith: "Starts with ",
  endsWith: "Ends with ",
  blank: "Blank",
  notBlank: "Not blank",
};

const formatAppliedFilterValue = (filterModel) => {
  if (!filterModel) return "-";

  if (filterModel.operator && (filterModel.condition1 || filterModel.condition2)) {
    return [filterModel.condition1, filterModel.condition2]
      .filter(Boolean)
      .map((condition) => formatAppliedFilterValue(condition))
      .join(` ${filterModel.operator} `);
  }

  const { filterType, type, filter, filterTo, dateFrom, dateTo } = filterModel;

  if (type === "inRange") {
    return filterType === "date"
      ? `Between ${dateFrom ?? ""} - ${dateTo ?? ""}`
      : `Between ${filter} - ${filterTo}`;
  }

  if (type === "blank" || type === "notBlank") {
    return filterOperatorLabels[type];
  }

  const value = filterType === "date" ? dateFrom ?? filter : filter;
  const label = filterOperatorLabels[type] ?? (type ? `${type} ` : "");
  return `${label}${value ?? ""}`.trim() || "-";
};

/** Deterministic stringify so filter dedupe is not broken by key order / referential equality. */
const stableStringify = (value) => {
  if (value === null || value === undefined) {
    return JSON.stringify(value);
  }
  if (typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
    .join(",")}}`;
};

const MartsActionsMenu = ({ onViewReports, onInputSummary, onDownloadMart }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const closeMenu = () => setIsOpen(false);
    document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  }, [isOpen]);

  return (
    <div className="marts-actions-dropdown">
      <button
        className="marts-btn"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        Actions
        <ChevronDown size={14} />
      </button>
      {isOpen && (
        <div className="marts-actions-menu" onClick={(e) => e.stopPropagation()}>
          <button
            className="marts-actions-menu-item"
            onClick={() => {
              setIsOpen(false);
              onViewReports();
            }}
          >
            View Reports
          </button>
          <button
            className="marts-actions-menu-item"
            onClick={() => {
              setIsOpen(false);
              onInputSummary();
            }}
          >
            Input Summary
          </button>
          <button
            className="marts-actions-menu-item"
            onClick={() => {
              setIsOpen(false);
              onDownloadMart();
            }}
          >
            Download Mart
          </button>
        </div>
      )}
    </div>
  );
};

const GridExample = () => {
  const location = useLocation();
  const projectId = location.state?.projectId || location.state?.project_id;
  const clientID = location.state?.clientID;
  const roleId = location.state?.roleId;
  const { inputID } = useParams();
  const martID = location.state?.mart_id;
  const isChildMart = Boolean(location.state?.isChildMart);
  const appliedFilterId =
    location.state?.filterId || location.state?.filter_id;
  const marts_name =
    location.state?.martsFileName || location.state?.projectName || "";
  const [url, setUrl] = useState();
  const gridRef = useRef();
  const gridContainerRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const columnDefsRef = useRef([]);
  /** Tracks the last applied column structure so columnDefs is only replaced when fields actually change, not on every filtered fetch (full replacement mid-render corrupts AG Grid's grouped header state). */
  const columnStructureSignatureRef = useRef(null);
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [currentFilterModel, setCurrentFilterModel] = useState({});
  /** Persists filter payload for API calls (must survive re-renders and reset on reload). */
  const displayFilterModelRef = useRef({});
  /** Skip duplicate backend fetches when AG Grid re-fires filterChanged after row/column updates. */
  const lastBackendFilterSignatureRef = useRef(null);
  const fetchDataRef = useRef(async () => {});
  const fetchInFlightRef = useRef(false);
  const backendReturnedEmptyRef = useRef(false);
  const [backendReturnedEmpty, setBackendReturnedEmpty] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    backendReturnedEmptyRef.current = backendReturnedEmpty;
  }, [backendReturnedEmpty]);

  useEffect(() => {
    columnDefsRef.current = columnDefs;
  }, [columnDefs]);

  const [martsFileName, setMartsFileName] = useState("");
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState("");
  const [selectedSheet, setSelectedSheet] = useState("");
  const [selectedColumn, setSelectedColumn] = useState("");
  const [sheetList, setSheetList] = useState([]);
  const [columnList, setColumnList] = useState([]);
  const [activeTab, setActiveTab] = useState("marts");
  const headerObserverRef = useRef(null);
  const [isGenerateMartModalOpen, setIsGenerateMartModalOpen] = useState(false);
  const [childFilterName, setChildFilterName] = useState("");
  const [isGeneratingChildMart, setIsGeneratingChildMart] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [activeAppliedFilters, setActiveAppliedFilters] = useState([]);
  const [isLoadingAppliedFilters, setIsLoadingAppliedFilters] = useState(false);
  const [isAppliedFiltersModalOpen, setIsAppliedFiltersModalOpen] = useState(false);

  const pageSize = 100;

  /**
   * Memoized so their references stay stable across renders. ag-grid-react rebuilds the entire
   * column/header model whenever defaultColDef/gridOptions/sideBar change identity, so a fresh
   * object literal here on every render (e.g. from opening the Generate Child Mart modal) was
   * corrupting AG Grid's grouped header state (see getProvidedColumnGroup crash).
   */
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
      tooltipShowDelay: 300,
      cellStyle: { padding: "8px 12px" },
      filterParams: {
        buttons: ["reset", "apply"],
        closeOnApply: true,
      },
    }),
    []
  );

  const gridOptions = useMemo(
    () => ({
      pivotMode: false,
      suppressScrollOnNewData: true,
      animateRows: true,
      enableRangeSelection: true,
      enableRangeHandle: true,
      theme: myTheme,
      getRowStyle: getRowStyle,
    }),
    []
  );

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          toolPanelParams: {
            suppressExpandAll: false,
            suppressFilterSearch: false,
            suppressSyncLayoutWithGrid: false,
          },
        },
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: false,
            suppressValues: false,
            suppressPivots: false,
            suppressPivotMode: false,
            suppressColumnFilter: false,
            suppressColumnSelectAll: false,
            suppressColumnExpandAll: false,
          },
        },
      ],
      defaultToolPanel: "none",
      hiddenByDefault: false,
      position: "right",
    }),
    []
  );

  useEffect(() => {
    const martsNameFromState =
      location.state?.projectName || location.state?.martsFileName;
    console.log("Marts name from state:", martsNameFromState);
    if (martsNameFromState) {
      setMartsFileName(martsNameFromState);
    }
  }, [location.state]);

  const fetchSummaryData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Unauthorized access. Please log in again.");
      }

      const response = await fetch(
        `${BASE_URL}/input/get_input_excel_summary`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(inputID),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch summary data: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Summary data response:", data);
      setSummaryData(data);

      if (data && data["File Name"]) {
        setFileName(data["File Name"]);
      }

      const sheets = Object.keys(data).filter((key) => key !== "File Name");
      setSheetList(sheets);

      if (sheets.length > 0) {
        setSelectedSheet(sheets[0]);
        const columns = Object.keys(data[sheets[0]]);
        setColumnList(columns);
        if (columns.length > 0) {
          setSelectedColumn(columns[0]);
        }
      }
    } catch (err) {
      console.error("Error fetching summary data:", err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSummaryModalOpen) {
      fetchSummaryData();
    }
  }, [isSummaryModalOpen]);

  const formatDateString = (value) => {
    if (typeof value === "string" && value.includes("T")) {
      return value.split("T")[0];
    }
    return value;
  };

  const fetchRawData = async (filterModel = null) => {
    if (fetchInFlightRef.current) {
      console.log("Fetch aborted: Already loading data");
      return null;
    }
    fetchInFlightRef.current = true;

    try {
      let url = `${BASE_URL}/reports/get_data_after_marts_with_skip_top_with_filter?input_id=${inputID}&mart_id=${martID}&skip=${skip}&top=${pageSize}&filters=${JSON.stringify(
        displayFilterModelRef.current,
        null,
        2
      )}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log("Server response:", data);
      if (!data || !Array.isArray(data)) {
        console.warn("Invalid data format received from server");
        return null;
      }
      return data;
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    } finally {
      fetchInFlightRef.current = false;
    }
  };

  const processData = (
    data,
    filterModel = null,
    reset = false,
    currentSkip = 0
  ) => {
    if (!Array.isArray(data) || data.length === 0) {
      if (reset) {
        setRowData([]);
        // Keep column structure when a filter returns no rows so headers remain and user can reload.
        setColumnDefs((prev) => (prev.length === 0 ? [] : prev));
      }
      return currentSkip;
    }

    const filteredData = data;

    let transformedData = [];
    let columnGroups = {};

    filteredData.forEach((record) => {
      const flat = {};
      if (record.employee_id) flat.employee_id = record.employee_id;

      Object.entries(record).forEach(([key, value]) => {
        if (key === "employee_id") return;

        if (typeof value === "object" && value !== null) {
          const monthPattern =
            /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{2}$/;
          const isMonthGroup = Object.keys(value).some((k) =>
            monthPattern.test(k)
          );

          if (isMonthGroup) {
            if (!columnGroups[key]) columnGroups[key] = [];
            Object.entries(value).forEach(([month, val]) => {
              const internalFieldName = `${key.replace(/\s+/g, "_")}_${month}`;
              flat[internalFieldName] = val ?? "-";
              if (
                !columnGroups[key].find(
                  (col) => col.field === internalFieldName
                )
              ) {
                columnGroups[key].push({
                  field: internalFieldName,
                  originalName: month,
                  section: key,
                });
              }
            });
          } else {
            Object.entries(value).forEach(([nestedKey, val]) => {
              const internalFieldName = `${key.replace(
                /\s+/g,
                "_"
              )}_${nestedKey.replace(/\s+/g, "_")}`;
              let displayValue = val ?? "-";

              if (nestedKey === "Employee Name") {
                displayValue =
                  displayValue !== "-" ? String(displayValue).trim() : "-";
              } else if (nestedKey === "Gender") {
                const gender = String(displayValue).trim().toLowerCase();
                displayValue = gender
                  ? gender.charAt(0).toUpperCase() + gender.slice(1)
                  : "-";
              } else if (isValidDate(displayValue)) {
                // Convert valid date strings to Date objects
                displayValue = convertToDateObject(displayValue);
              }

              flat[internalFieldName] = displayValue;
              if (!columnGroups[key]) columnGroups[key] = [];
              if (
                !columnGroups[key].find(
                  (col) => col.field === internalFieldName
                )
              ) {
                columnGroups[key].push({
                  field: internalFieldName,
                  originalName: nestedKey,
                  section: key,
                });
              }
            });
          }
        } else {
          let displayValue = value ?? "-";

          if (isValidDate(displayValue)) {
            // Convert valid date strings to Date objects
            displayValue = convertToDateObject(displayValue);
          }

          flat[key] = displayValue;
          if (!columnGroups["General Info"]) columnGroups["General Info"] = [];
          if (!columnGroups["General Info"].find((col) => col.field === key)) {
            columnGroups["General Info"].push({
              field: key,
              originalName: key,
              section: "General Info",
            });
          }
        }
      });
      transformedData.push(flat);
    });

    let dynamicColumns = Object.keys(columnGroups).map((group) => ({
      groupId: group,
      headerName: group,
      headerTooltip: group,
      children: columnGroups[group].map((colInfo) => {
        const field = colInfo.field;
        const originalName = colInfo.originalName;
        const sampleValue = transformedData[0]
          ? transformedData[0][field]
          : null;

        // Check data type based on sample values from multiple rows
        const sampleValues = transformedData
          .slice(0, 10)
          .map((row) => row[field])
          .filter((val) => val !== "-" && val != null);
        const isNumber =
          sampleValues.length > 0 &&
          sampleValues.every((val) => !isNaN(val) && typeof val === "number");
        const isDate =
          sampleValues.length > 0 &&
          sampleValues.some((val) => val instanceof Date || isValidDate(val));
        const isDateTime =
          isDate &&
          sampleValues.some(
            (val) =>
              (val instanceof Date && val.getHours() !== 0) ||
              (typeof val === "string" && val.includes("T"))
          );

        let columnDef = {
          field,
          headerName: originalName,
          headerTooltip: originalName,
          sortable: true,
          floatingFilter: true,
          resizable: true,
          enablePivot: true,
          enableRowGroup: true,
          enableValue: true,
          enableRangeSelection: true,
        };

        // Configure filter and formatter based on data type
        if (isDateTime) {
          // DateTime column - force date filter
          columnDef.filter = "agDateColumnFilter";
          columnDef.valueFormatter = dateValueFormatter;
          columnDef.filterParams = {
            browserDatePicker: true,
            comparator: dateComparator,
            buttons: ["reset", "apply"],
            closeOnApply: true,
            // Force date filter over set filter
            suppressSetFilterList: true,
          };
          columnDef.aggFunc = "count";
          columnDef.chartDataType = "category";
        } else if (isDate) {
          // Date column - force date filter
          columnDef.filter = "agDateColumnFilter";
          columnDef.valueFormatter = dateValueFormatter;
          columnDef.filterParams = {
            browserDatePicker: true,
            comparator: dateComparator,
            buttons: ["reset", "apply"],
            closeOnApply: true,
            // Force date filter over set filter
            suppressSetFilterList: true,
          };
          columnDef.aggFunc = "count";
          columnDef.chartDataType = "category";
        } else if (isNumber) {
          // Number column
          columnDef.filter = "agNumberColumnFilter";
          columnDef.aggFunc = "sum";
          columnDef.chartDataType = "series";
          columnDef.valueFormatter = (params) => {
            if (params.value !== null && params.value !== undefined) {
              return params.value.toLocaleString();
            }
            return params.value;
          };
          columnDef.filterParams = {
            buttons: ["reset", "apply"],
            closeOnApply: true,
          };
        } else {
          // Text column
          columnDef.filter = "agTextColumnFilter";
          columnDef.aggFunc = "count";
          columnDef.chartDataType = "category";
          columnDef.filterParams = {
            buttons: ["reset", "apply"],
            closeOnApply: true,
          };
        }

        // Special handling for Employee ID
        if (
          field === "employee_id" ||
          originalName === "Employee ID" ||
          originalName === "Employee_ID"
        ) {
          columnDef.pinned = "left";
          columnDef.lockPinned = true;
          columnDef.suppressMovable = true;
          columnDef.suppressColumnsToolPanel = true;
          columnDef.width = 200;
          columnDef.headerClass = "highlighted-column";
        }

        return columnDef;
      }),
    }));

    setRowData((prev) => {
      const newData = reset ? transformedData : [...prev, ...transformedData];
      return newData;
    });

    if (reset || columnDefs.length === 0) {
      const structureSignature = JSON.stringify(
        dynamicColumns.map((group) => ({
          groupId: group.groupId,
          fields: group.children.map((child) => child.field),
        }))
      );
      if (structureSignature !== columnStructureSignatureRef.current) {
        columnStructureSignatureRef.current = structureSignature;
        setColumnDefs(dynamicColumns);
      }
    }

    if (!reset) {
      setSkip((prev) => prev + pageSize);
    }
  };

  const fetchData = async (
    filterModel = null,
    reset = false,
    explicitSkip = null
  ) => {
    setIsLoading(true);
    const data = await fetchRawData(filterModel);
    if (Array.isArray(data)) {
      processData(data, filterModel, reset, explicitSkip ?? skip);
      if (reset) {
        setBackendReturnedEmpty(data.length === 0);
        lastBackendFilterSignatureRef.current = stableStringify(
          displayFilterModelRef.current
        );
      }
    } else {
      console.warn("No data returned from server");
      if (reset) {
        lastBackendFilterSignatureRef.current = null;
        setRowData([]);
        setColumnDefs([]);
        setBackendReturnedEmpty(false);
      }
    }
    setIsLoading(false);
    setIsInitialLoad(false);
  };

  const handleReloadGridData = async () => {
    setBackendReturnedEmpty(false);
    lastBackendFilterSignatureRef.current = null;
    displayFilterModelRef.current = {};
    setSkip(0);
    setCurrentFilterModel({});
    gridRef.current?.api?.setFilterModel(null);
    await fetchData(null, true, 0);
  };

  fetchDataRef.current = fetchData;

  /**
   * Must be stable across renders — creating debounce() inside the component body breaks dedupe
   * because every re-render gets a new debounced function.
   */
  const onFilterChangedDebounced = useMemo(
    () =>
      debounce(async (params) => {
        if (!params.api) {
          return;
        }

        const filterModel = params.api.getFilterModel();
        setCurrentFilterModel(filterModel);

        const getOriginalFieldName = (internalFieldName, colDefs) => {
          for (const colDef of colDefs) {
            if (colDef.children) {
              for (const child of colDef.children) {
                if (child.field === internalFieldName) {
                  return `${colDef.headerName}.${child.headerName}`;
                }
              }
            } else if (colDef.field === internalFieldName) {
              return colDef.headerName;
            }
          }
          return internalFieldName;
        };

        const colDefs = columnDefsRef.current;
        displayFilterModelRef.current = {};
        const filterRows = Object.entries(filterModel).map(([key, model]) => {
          const originalFieldName = getOriginalFieldName(key, colDefs);
          displayFilterModelRef.current[originalFieldName] = filterModel[key];
          const dotIdx = originalFieldName.indexOf(".");
          return {
            main_header: dotIdx >= 0 ? originalFieldName.slice(0, dotIdx) : "-",
            column:
              dotIdx >= 0
                ? originalFieldName.slice(dotIdx + 1)
                : originalFieldName,
            applied_filter: formatAppliedFilterValue(model),
          };
        });
        setActiveAppliedFilters(filterRows);

        const filterSignature = stableStringify(displayFilterModelRef.current);
        if (filterSignature === lastBackendFilterSignatureRef.current) {
          return;
        }
        lastBackendFilterSignatureRef.current = filterSignature;

        setSkip(0);
        await fetchDataRef.current(filterModel, true, 0);

        console.log(
          `Filters applied: ${JSON.stringify(displayFilterModelRef.current, null, 2)}`
        );
      }, 300),
    []
  );

  /** Alias for AG Grid prop; keeps name aligned with `onFilterChanged` event API. */
  const onFilterChanged = onFilterChangedDebounced;

  useEffect(() => {
    return () => onFilterChangedDebounced.cancel();
  }, [onFilterChangedDebounced]);

  useEffect(() => {
    fetchData(null, true);
  }, []);

  const handlemartdownload = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Unauthorized access. Please log in again.");
      }
      const response = await fetch(
        `${BASE_URL}/mart_router/generate_marts_for_id`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            input_id: inputID,
            mart_id: martID,
            marts_name: marts_name,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch error report: ${response.statusText}`);
      }

      const data = await response.json();
      setUrl(data.url || null);

      if (data.url) {
        const link = document.createElement("a");
        link.href = data.url;
        link.download = "marts.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error("Error fetching error report:", err.message);
    }
  };

  const onGridReady = (params) => {
    gridRef.current = { api: params.api, columnApi: params.columnApi };
    if (!params.columnApi) {
      return;
    }
    setTimeout(() => {
      const allColumnIds =
        params.columnApi.getAllColumns()?.map((column) => column.getId()) || [];
      if (allColumnIds.length > 0) {
        params.columnApi.autoSizeColumns(allColumnIds);
      }
    }, 100);
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.columnApi) {
      const allColumnIds =
        gridRef.current.columnApi.getAllColumns()?.map((col) => col.getId()) ||
        [];
      if (allColumnIds.length > 0) {
        gridRef.current.columnApi.autoSizeColumns(allColumnIds);
      }
    }
  }, [rowData]);

  const applyHeaderTitleAttributes = () => {
    if (!gridContainerRef.current) return;
    const headerTextNodes = gridContainerRef.current.querySelectorAll(
      ".ag-header-cell-text, .ag-header-group-text"
    );
    headerTextNodes.forEach((node) => {
      const text = node.textContent?.trim();
      if (text) {
        node.setAttribute("title", text);
      }
    });
  };

  // Keep native hover tooltips stable even when AG Grid re-renders headers.
  useEffect(() => {
    if (!gridContainerRef.current) return;

    applyHeaderTitleAttributes();
    const timeoutId = setTimeout(applyHeaderTitleAttributes, 0);

    const headerRoot =
      gridContainerRef.current.querySelector(".ag-header") ||
      gridContainerRef.current;
    if (!headerRoot) return () => clearTimeout(timeoutId);

    if (headerObserverRef.current) {
      headerObserverRef.current.disconnect();
    }

    headerObserverRef.current = new MutationObserver(() => {
      applyHeaderTitleAttributes();
    });

    headerObserverRef.current.observe(headerRoot, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      clearTimeout(timeoutId);
      if (headerObserverRef.current) {
        headerObserverRef.current.disconnect();
        headerObserverRef.current = null;
      }
    };
  }, [columnDefs]);

  useEffect(() => {
    if (!gridContainerRef.current || !gridRef.current?.api) {
      return;
    }
    const viewport =
      gridContainerRef.current.querySelector(".ag-body-viewport") ||
      gridContainerRef.current.querySelector(".ag-body") ||
      gridContainerRef.current.querySelector(".ag-center-cols-viewport");
    if (!viewport) {
      return;
    }
    const handleScroll = () => {
      const scrollTop = viewport.scrollTop;
      const scrollHeight = viewport.scrollHeight;
      const clientHeight = viewport.clientHeight;
      if (
        scrollTop + clientHeight >= scrollHeight - 100 &&
        !isLoading &&
        !backendReturnedEmptyRef.current
      ) {
        const filterModel = gridRef.current.api.getFilterModel();
        fetchData(filterModel, false);
      }
    };
    viewport.addEventListener("scroll", handleScroll);
    return () => {
      viewport.removeEventListener("scroll", handleScroll);
    };
  }, [isLoading, gridRef.current?.api]);

  const listofreports = () => {
    navigate(`/architect/${inputID}/reports_list`, {
      state: {
        projectName: martsFileName,
        mart_id: martID,
        projectId,
        inputID,
        clientID,
        roleId,
      },
    });
    console.log("Button clicked");
  };

  const hasActiveFilters =
    currentFilterModel && Object.keys(currentFilterModel).length > 0;
  const canGenerateChildMart =
    hasActiveFilters && rowData.length > 0 && !backendReturnedEmpty;
  const filtersToDisplay =  activeAppliedFilters.length > 0
                        ? activeAppliedFilters
                        : appliedFilters;
  const shouldShowFilterSummary =
    Boolean(appliedFilterId) || activeAppliedFilters.length > 0;

  const handleGenerateChildMart = () => {
    setIsGenerateMartModalOpen(true);
  };

  const handleConfirmGenerateChildMart = async () => {
    if (!childFilterName.trim()) return;
    setIsGeneratingChildMart(true);
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Unauthorized access. Please log in again.");
      }

      const response = await fetch(
        `${BASE_URL}/mart_router/save_filter_and_generate_marts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            input_id: inputID,
            parent_mart_id: martID,
            filter_name: childFilterName,
            filters: displayFilterModelRef.current,
            marts_name: martsFileName,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to generate child mart: ${response.statusText}`);
      }

      await response.json();
      messageApi.success("Child mart generated successfully.");
      setIsGenerateMartModalOpen(false);
      setChildFilterName("");
    } catch (err) {
      console.error("Error generating child mart:", err.message);
      messageApi.error("Failed to generate child mart. Please try again.");
    } finally {
      setIsGeneratingChildMart(false);
    }
  };

  const fetchAppliedFilters = async () => {
    if (!appliedFilterId) return;
    setIsLoadingAppliedFilters(true);
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Unauthorized access. Please log in again.");
      }

      const response = await fetch(
        `${BASE_URL}/mart_router/get_filter_details?filter_id=${appliedFilterId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch applied filters");
      }

      const data = await response.json();
      const rawFilters = data.filters || data.filter_details || {};
      const list = Array.isArray(rawFilters)
        ? rawFilters
        : Object.entries(rawFilters).map(([key, filterModel]) => {
            const dotIdx = key.indexOf(".");
            const mainHeader = dotIdx >= 0 ? key.slice(0, dotIdx) : key;
            const column = dotIdx >= 0 ? key.slice(dotIdx + 1) : key;
            return {
              main_header: mainHeader,
              column,
              applied_filter: formatAppliedFilterValue(filterModel),
            };
          });
      setAppliedFilters(list);
    } catch (err) {
      console.error("Error fetching applied filters:", err.message);
    } finally {
      setIsLoadingAppliedFilters(false);
    }
  };

  useEffect(() => {
    fetchAppliedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilterId]);

  const handleViewChildMarts = () => {
    navigate(`/architect/${inputID}/allmarts`, {
      state: {
        projectId,
        inputID,
        clientID,
        roleId,
        isChildMartsView: true,
        parentMartId: martID,
        parentMartName: martsFileName,
      },
    });
  };

  const handleback = () => {
    navigate(`/architect/${inputID}/allmarts`, {
      state: {
        projectId: projectId,
        projectName: martsFileName || location.state?.projectName,
        inputID,
        clientID,
        roleId,
      },
    });
  };

  const toggleSummaryModal = () => {
    setIsSummaryModalOpen(!isSummaryModalOpen);
  };

  const handleSheetChange = (e) => {
    const sheet = e.target.value;
    setSelectedSheet(sheet);
    if (summaryData && summaryData[sheet]) {
      const columns = Object.keys(summaryData[sheet]);
      setColumnList(columns);
      setSelectedColumn(columns.length > 0 ? columns[0] : "");
    } else {
      setColumnList([]);
      setSelectedColumn("");
    }
  };

  const getCurrentData = () => {
    if (!summaryData || !selectedSheet || !selectedColumn) {
      return null;
    }
    return summaryData[selectedSheet][selectedColumn] || null;
  };

  const currentData = getCurrentData();
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="marts-containerrr">
      {contextHolder}
      <div className="marts-main-content">
        <div className="marts-header">
          <button className="marts-header-left" onClick={handleback}>
            <ArrowBackIosOutlinedIcon sx={{ fontSize: "16px" }} />
            <h1 className="marts-title">{martsFileName}</h1>
          </button>
          <div className="marts-header-right">
            {!isChildMart && canGenerateChildMart && (
              <button className="Generate-child-marts-btn" onClick={handleGenerateChildMart}>
                Generate Child Mart
              </button>
            )}
            {!isChildMart && (
              <button className="marts-btn" onClick={handleViewChildMarts}>
                View Child Marts
              </button>
            )}  
            <MartsActionsMenu
              onViewReports={listofreports}
              onInputSummary={toggleSummaryModal}
              onDownloadMart={handlemartdownload}
            />
          </div>
        </div>

        {shouldShowFilterSummary && (
          <div className="marts-filters-summary-row">
            <span className="marts-filters-summary-text">
              Filters Applied: {String(filtersToDisplay.length).padStart(2, "0")}
            </span>
            <button
              className="marts-btn"
              onClick={() => setIsAppliedFiltersModalOpen(true)}
              disabled={Boolean(appliedFilterId) && isLoadingAppliedFilters}
            >
              View
            </button>
          </div>
        )}

        <FiltersSection activeTab={activeTab} onTabChange={handleTabChange} />

        <div className="marts-grid-container" style={{ position: "relative" }}>
          <div
            className="marts-ag-theme-alpine ag-theme-alpine"
            style={{
              height: "93%",
              width: "100%",
              position: "relative",
            }}
            ref={gridContainerRef}
          >
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              {...gridOptions}
              pagination={true}
              onGridReady={onGridReady}
              onFilterChanged={onFilterChanged}
              onFirstDataRendered={applyHeaderTitleAttributes}
              onDisplayedColumnsChanged={applyHeaderTitleAttributes}
              onColumnResized={applyHeaderTitleAttributes}
              onColumnMoved={applyHeaderTitleAttributes}
              enableCharts={true}
              sideBar={sideBar}
              groupDisplayType="multipleColumns"
              rowGroupPanelShow="always"
              pivotPanelShow="always"
              enableRangeHandle={true}
              suppressMultiRangeSelection={false}
              allowDragFromColumnsToolPanel={true}
              rowHeight={38}
              headerHeight={38}
              domLayout="normal"
              enableBrowserTooltips={true}
              tooltipShowMode="always"
              tooltipShowDelay={100}
              tooltipHideDelay={1500}
            />
            {isLoading && (
              <div className="grid-loading-overlay">
                <div className="loading-content">
                  <div className="loading-spinner"></div>
                  <div className="loading-text">
                    {isInitialLoad ? "Loading data..." : "Loading more data..."}
                  </div>
                </div>
              </div>
            )}
            {backendReturnedEmpty && !isLoading && (
              <div className="marts-grid-empty-overlay" role="status">
                <div className="marts-grid-empty-card">
                  <p className="marts-grid-empty-title">No data returned</p>
                  <p className="marts-grid-empty-text">
                    The server returned no rows for the current filters (or there is no data yet).
                    Reload to fetch again without filters.
                  </p>
                  <button
                    type="button"
                    className="marts-btn marts-grid-empty-reload"
                    onClick={handleReloadGridData}
                  >
                    Reload data
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {isSummaryModalOpen && (
          <div className="summary-modal-overlay" onClick={toggleSummaryModal}>
            <div className="summary-modal" onClick={(e) => e.stopPropagation()}>
              <div className="summary-header">
                <button className="summary-title" onClick={toggleSummaryModal}>
                  <span className="summary-back-arrow">
                    <ChevronLeft />
                  </span>
                  <h1>Summary Report</h1>
                </button>
              </div>

              <div className="summary-file-selection">
                <div className="summary-input-file">
                  <label>File Name</label>
                  <div className="summary-select-file">
                    <input
                      type="text"
                      value={fileName || ""}
                      readOnly
                      disabled
                      className="summary-select"
                      style={{
                        backgroundColor: "#FFFFFF",
                        cursor: "not-allowed",
                      }}
                    />
                  </div>
                </div>
                <div className="summary-input-group">
                  <label>Select Sheet</label>
                  <div className="summary-select-wrapper">
                    <select
                      value={selectedSheet}
                      onChange={handleSheetChange}
                      className="summary-select"
                    >
                      {sheetList.map((sheet) => (
                        <option key={sheet} value={sheet}>
                          {sheet}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="summary-input-group">
                  <label>Select Column</label>
                  <div className="summary-select-wrapper">
                    <select
                      value={selectedColumn}
                      onChange={(e) => setSelectedColumn(e.target.value)}
                      className="summary-select"
                    >
                      {columnList.map((column) => (
                        <option key={column} value={column}>
                          {column}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="summary-loading">Loading data...</div>
              ) : error ? (
                <div className="summary-error">Error: {error}</div>
              ) : (
                <div className="summary-stats-container">
                  <div className="summary-stats-row">
                    {currentData && (
                      <>
                        <div
                          className={`summary-stat-card ${currentData["Total Rows"] !== null
                              ? "summary-red-card"
                              : "summary-black-card"
                            }`}
                        >
                          <span className="summary-stat-value">
                            {currentData["Total Rows"] !== null
                              ? currentData["Total Rows"]
                              : 0}
                          </span>
                          <span className="summary-stat-label">Total Rows</span>
                        </div>
                        <div
                          className={`summary-stat-card ${currentData["Total Zeros"] !== null
                              ? "summary-red-card"
                              : "summary-black-card"
                            }`}
                        >
                          <span className="summary-stat-value">
                            {currentData["Total Zeros"] !== null
                              ? currentData["Total Zeros"]
                              : 0}
                          </span>
                          <span className="summary-stat-label">
                            Total Zeros
                          </span>
                        </div>
                        <div
                          className={`summary-stat-card ${currentData["Total blanks"] !== null
                              ? "summary-red-card"
                              : "summary-black-card"
                            }`}
                        >
                          <span className="summary-stat-value">
                            {currentData["Total blanks"] !== null
                              ? currentData["Total blanks"]
                              : 0}
                          </span>
                          <span className="summary-stat-label">
                            Total Blanks
                          </span>
                        </div>
                        <div
                          className={`summary-stat-card ${currentData["Total NA values"] !== null
                              ? "summary-red-card"
                              : "summary-black-card"
                            }`}
                        >
                          <span className="summary-stat-value">
                            {currentData["Total NA values"] !== null
                              ? currentData["Total NA values"]
                              : 0}
                          </span>
                          <span className="summary-stat-label">
                            Total "NA" Values
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="summary-stats-row">
                    {currentData && (
                      <>
                        <div
                          className={`summary-stat-card ${currentData["Max"] !== null
                              ? "summary-red-card"
                              : "summary-black-card"
                            }`}
                        >
                          <span className="summary-stat-value">
                            {currentData["Max"] !== null
                              ? currentData["Max"]
                              : "--"}
                          </span>
                          <span className="summary-stat-label">
                            Maximum Value
                          </span>
                        </div>
                        <div
                          className={`summary-stat-card ${currentData["Min"] !== null
                              ? "summary-red-card"
                              : "summary-black-card"
                            }`}
                        >
                          <span className="summary-stat-value">
                            {currentData["Min"] !== null
                              ? currentData["Min"]
                              : "--"}
                          </span>
                          <span className="summary-stat-label">
                            Minimum Value
                          </span>
                        </div>
                        <div
                          className={`summary-stat-card ${currentData["Total -ve values"] !== null
                              ? "summary-red-card"
                              : "summary-black-card"
                            }`}
                        >
                          <span className="summary-stat-value">
                            {currentData["Total -ve values"] !== null
                              ? currentData["Total -ve values"]
                              : "--"}
                          </span>
                          <span className="summary-stat-label">
                            Total Negative Values
                          </span>
                        </div>
                        <div
                          className={`summary-stat-card ${currentData["Median"] !== null
                              ? "summary-red-card"
                              : "summary-black-card"
                            }`}
                        >
                          <span className="summary-stat-value">
                            {currentData["Median"] !== null
                              ? currentData["Median"]
                              : "--"}
                          </span>
                          <span className="summary-stat-label">
                            Median Value
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="summary-stats-row">
                    {currentData && (
                      <div
                        className={`summary-stat-card ${currentData["Avg"] !== null
                            ? "summary-red-card"
                            : "summary-black-card"
                          }`}
                      >
                        <span className="summary-stat-value">
                          {currentData["Avg"] !== null
                            ? currentData["Avg"]
                            : "--"}
                        </span>
                        <span className="summary-stat-label">
                          Average Value
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {isGenerateMartModalOpen && (
          <div
            className="generate-mart-modal-overlay"
            onClick={() => {
              if (!isGeneratingChildMart) {
                setIsGenerateMartModalOpen(false);
              }
            }}
          >
            <div
              className="generate-mart-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="generate-mart-modal-header">
                <h2 className="generate-mart-modal-title">
                  Generate Child Mart
                </h2>
              </div>
              <div className="generate-mart-modal-content">
                <div className="generate-mart-input-group">
                  <label>Child Mart Name</label>
                  <input
                    type="text"
                    value={childFilterName}
                    onChange={(e) => setChildFilterName(e.target.value)}
                    className="generate-mart-input"
                    placeholder="Enter child mart name"
                    autoFocus
                  />
                </div>
              </div>
              <div className="generate-mart-modal-actions">
                <button
                  className="generate-mart-btn generate-mart-btn-confirm"
                  onClick={handleConfirmGenerateChildMart}
                  disabled={!childFilterName.trim() || isGeneratingChildMart}
                >
                  {isGeneratingChildMart ? "Generating..." : "Generate"}
                </button>
                <button
                  className="generate-mart-btn generate-mart-btn-cancel"
                  onClick={() => {
                    setIsGenerateMartModalOpen(false);
                    setChildFilterName("");
                  }}
                  disabled={isGeneratingChildMart}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {isAppliedFiltersModalOpen && (
          <div
            className="applied-filters-modal-overlay"
            onClick={() => setIsAppliedFiltersModalOpen(false)}
          >
            <div
              className="applied-filters-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="applied-filters-modal-header">
                <div>
                  <h2 className="applied-filters-modal-title">
                    Applied Filters
                  </h2>
                  <p className="applied-filters-modal-subtitle">
                    Filters applied:{" "}
                    {String(filtersToDisplay.length).padStart(2, "0")}
                  </p>
                </div>
                <button
                  className="applied-filters-modal-close"
                  onClick={() => setIsAppliedFiltersModalOpen(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="applied-filters-modal-body">
                <table className="applied-filters-table">
                  <thead>
                    <tr>
                      <th>Main Header</th>
                      <th>Column</th>
                      <th>Applied Filter</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtersToDisplay.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          {row.main_header ??
                            row.mainHeader ??
                            row["Main Header"] ??
                            "-"}
                        </td>
                        <td>{row.column ?? row.Column ?? "-"}</td>
                        <td>
                          {row.applied_filter ??
                            row.appliedFilter ??
                            row.value ??
                            row["Applied Filter"] ??
                            "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="applied-filters-modal-actions">
                <button
                  className="applied-filters-btn applied-filters-btn-confirm"
                  onClick={() => setIsAppliedFiltersModalOpen(false)}
                >
                  OK
                </button>
                <button
                  className="applied-filters-btn applied-filters-btn-reset"
                  onClick={() => setIsAppliedFiltersModalOpen(false)}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GridExample;
